import { Alert, Button, Center, Loader, Stack, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useAuth } from "@/features/auth/lib/use-auth";
import { StaffLoginModal } from "@/features/auth/ui/login-modal";
import { fetchCardCheck } from "@/features/card/api/card-api";
import { isCardCode } from "@/features/card/lib/card-code";
import { groupRatesByPercent } from "@/features/card/lib/group-rates";
import { rememberCardCode } from "@/features/card/lib/last-card";
import { CardShell } from "@/features/card/ui/card-shell";

export function CheckPage() {
	const { code } = useParams<{ code: string }>();
	const validCode = isCardCode(code);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { canRedeem } = useAuth();
	const [loginOpen, setLoginOpen] = useState(false);

	useEffect(() => {
		if (validCode) {
			rememberCardCode(code);
		}
	}, [code, validCode]);

	const query = useQuery({
		queryKey: ["card", code],
		queryFn: () => fetchCardCheck(code as string),
		enabled: validCode,
		staleTime: 0,
		gcTime: 0,
		refetchOnWindowFocus: true,
	});

	if (!validCode) {
		return (
			<CardShell title={t("check.title")}>
				<Alert color="orange" title={t("check.missingCodeTitle")}>
					{t("check.missingCode")}
				</Alert>
			</CardShell>
		);
	}

	if (query.isLoading) {
		return (
			<CardShell title={t("check.title")}>
				<Center py="xl">
					<Loader />
				</Center>
			</CardShell>
		);
	}

	if (query.isError) {
		return (
			<CardShell title={t("check.title")}>
				<Alert color="red" title={t("check.loadError")}>
					<Stack gap="sm">
						<Text size="sm">{t("errors.network")}</Text>
						<Button variant="light" onClick={() => void query.refetch()}>
							{t("common.retry")}
						</Button>
					</Stack>
				</Alert>
			</CardShell>
		);
	}

	const card = query.data;
	if (!card) {
		return (
			<CardShell title={t("check.title")}>
				<Center py="xl">
					<Loader />
				</Center>
			</CardShell>
		);
	}
	const valid = card.status === "VALID";
	const groups = valid ? groupRatesByPercent(card.rates) : [];

	return (
		<CardShell
			title={t("check.title")}
			footer={
				valid && canRedeem ? (
					<Button
						fullWidth
						size="md"
						onClick={() => navigate(`/${code}/rewards`)}
					>
						{t("check.redeem")}
					</Button>
				) : valid ? (
					<Button
						fullWidth
						size="md"
						variant="light"
						onClick={() => setLoginOpen(true)}
					>
						{t("check.signInToRedeem")}
					</Button>
				) : null
			}
		>
			<StaffLoginModal
				opened={loginOpen}
				onClose={() => setLoginOpen(false)}
				onSignedIn={() => {
					setLoginOpen(false);
					navigate(`/${code}/rewards`);
				}}
			/>
			<Stack gap="lg" pt="sm">
				<Alert
					color={valid ? "playGreen" : "red"}
					title={valid ? t("check.valid") : t("check.invalid")}
				>
					{valid ? t("check.validHint") : t("check.invalidHint")}
				</Alert>

				{valid ? (
					<>
						<Stack gap={0}>
							<Text
								fw={700}
								lh={1}
								style={{
									fontSize: 56,
									color: "var(--mantine-primary-color-filled)",
								}}
							>
								{card.bonusBalance}
							</Text>
							<Text c="dimmed">{t("check.bonuses")}</Text>
						</Stack>

						{groups.length > 0 ? (
							<Stack gap="md">
								<Text fw={600}>{t("check.rates")}</Text>
								{groups.map((group) => (
									<Stack key={group.percent} gap={6}>
										<Text fw={700} size="sm">
											{group.percent}%
										</Text>
										{group.positions.map((position) => (
											<Text key={position} size="sm">
												{position}
											</Text>
										))}
									</Stack>
								))}
							</Stack>
						) : (
							<Alert color="gray">{t("check.noRates")}</Alert>
						)}
					</>
				) : null}
			</Stack>
		</CardShell>
	);
}
